import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { SEOWebPage } from '@/components/base/SEOSchema';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const activeSlug = currentCategory.slug;

  const filteredComics = useMemo(() => {
    let list = comics.filter((c) => c.category === activeSlug);
    if (statusFilter !== 'all') {
      list = list.filter((c) => c.status === statusFilter);
    }
    if (sortBy === 'latest') {
      list = [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    } else if (sortBy === 'hot') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [activeSlug, statusFilter, sortBy]);

  return (
    <>
      <SEOWebPage
        title={`${currentCategory.name}韩漫-${currentCategory.description}-51漫画网`}
        description={`51漫画网${currentCategory.name}分类页面，提供${currentCategory.description}的韩漫全集免费阅读。收录多部${currentCategory.name}题材优质韩漫作品。`}
        url={`https://ytzshn.cn/category/${activeSlug}`}
        breadcrumbItems={[
          { name: '首页', url: 'https://ytzshn.cn' },
          { name: currentCategory.name, url: `https://ytzshn.cn/category/${activeSlug}` },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]} />

          {/* Category Header */}
          <div className="py-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {currentCategory.name}
              <span className="text-lg font-normal text-foreground-500 ml-2">韩漫</span>
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${cat.slug === activeSlug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8 p-4 rounded-xl bg-background-100">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500">状态:</span>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-200/70 rounded-full">
                {(['all', '连载中', '已完结'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${statusFilter === s ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    {s === 'all' ? '全部' : s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500">排序:</span>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-200/70 rounded-full">
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'hot' as const, label: '最热' },
                  { key: 'rating' as const, label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${sortBy === opt.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 mb-12">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无符合条件的漫画</p>
              <Link to="/category/romance" className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium">
                浏览其他分类
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}